import { useCallback, useContext } from 'react';

import { getIndexName } from '@/integrations/algolia/helpers';
import { sendAlgoliaEvent } from '@/integrations/algolia/sendAlgoliaEvent';
import type { AlgoliaEvent } from '@/integrations/algolia/types';
import { AlgoliaEventsNames } from '@/integrations/algolia/types';
import { captureError } from '@/lib/errors';
import { LocaleContext } from '@/providers/LocaleProvider/LocaleContext';
import { useAlgoliaInsightsEnabled } from '@/providers/ThirdPartyFlagsProvider/ThirdPartyFlagsContext';

/**
 * Flag-aware wrapper around sendAlgoliaEvent.
 * Use this hook in all React components instead of calling sendAlgoliaEvent
 * directly. It reads the current Algolia disable flag from context and silently
 * drops the event when analytics are disabled.
 */
export const useSendAlgoliaEvent = () => {
  const isEnabled = useAlgoliaInsightsEnabled();

  return useCallback(
    (event: AlgoliaEvent) => {
      if (!isEnabled) return;
      try {
        sendAlgoliaEvent(event);
      } catch (error) {
        // search-insights throws when an event is sent before `init` has run.
        captureError(error as Error, { extra: { event: event.event } });
      }
    },
    [isEnabled],
  );
};

/** Sends a product click from a list that is not an Algolia search result. */
export const useSendClickedAlgoliaEvent = () => {
  const sendAlgoliaEvent = useSendAlgoliaEvent();
  const locale = useContext(LocaleContext);

  return useCallback(
    (objectID: string) => {
      sendAlgoliaEvent({
        event: AlgoliaEventsNames.clickedObjectIDs,
        indexName: getIndexName('PRODUCT', locale),
        objectIDs: [objectID],
      });
    },
    [locale, sendAlgoliaEvent],
  );
};

/**
 * Sends the add-to-cart conversion for one product. Without a queryID, the search
 * that led to the product is attributed by search-insights from the queryID it
 * cached when the product was clicked.
 */
export const useSendAddedToCartAlgoliaEvent = () => {
  const sendAlgoliaEvent = useSendAlgoliaEvent();
  const locale = useContext(LocaleContext);

  return useCallback(
    (objectID: string, price?: number, currency?: string, queryID?: string) => {
      sendAlgoliaEvent({
        event: AlgoliaEventsNames.addedToCartObjectIDs,
        indexName: getIndexName('PRODUCT', locale),
        objectIDs: [objectID],
        objectData: [{ queryID, price, quantity: 1 }],
        value: price,
        currency,
      });
    },
    [locale, sendAlgoliaEvent],
  );
};
